/**
 * useEmailVerification Hook
 *
 * Manages email verification state and actions.
 * - Polls Firebase every 5 seconds to detect when the user verifies their email.
 * - On detection, dispatches Redux actions so App.js routes the user automatically
 *   (no page reload, no memory leaks — all intervals are cleaned up on unmount).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import firebase from "../config/firebase";
import { loginSuccessFull } from "../store/actions/authActions";
import emailVerificationService from "../services/emailVerificationService";

const POLL_INTERVAL_MS = 5000; // check every 5 seconds

export const useEmailVerification = () => {
  const dispatch = useDispatch();
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const pollingRef = useRef(null);

  // Cooldown countdown
  useEffect(() => {
    const tick = () => {
      const remaining = emailVerificationService.getCooldownRemaining();
      setCooldownSeconds(Math.ceil(remaining / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Populate user email on mount
  useEffect(() => {
    const email = emailVerificationService.getCurrentUserEmail();
    if (email) setUserEmail(email);
  }, []);

  // Auto-poll for email verification — no reload, no memory leak
  useEffect(() => {
    const check = async () => {
      const user = firebase.auth().currentUser;
      if (!user) return;

      try {
        await user.reload();
        const fresh = firebase.auth().currentUser;
        if (fresh && fresh.emailVerified) {
          // Stop polling immediately
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          emailVerificationService.clearCooldown();
          toast.success("Email verified! Redirecting…");

          // Let the existing Redux flow handle routing
          dispatch({ type: "CLEAR_VERIFICATION_STATE" });
          dispatch(loginSuccessFull(fresh.uid));
        }
      } catch (err) {
        // Silently ignore transient network errors during polling
        console.error("Verification poll error:", err);
      }
    };

    pollingRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [dispatch]);

  /**
   * Resend verification email (user-triggered)
   */
  const resendVerificationEmail = useCallback(async () => {
    setIsResending(true);
    try {
      const result = await emailVerificationService.sendVerificationEmail();
      if (result.success) {
        toast.success("Verification email sent! Please check your inbox.");
      } else {
        toast.error(result.error || "Failed to send verification email");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
      console.error("Resend error:", error);
    } finally {
      setIsResending(false);
    }
  }, []);

  return {
    userEmail,
    isResending,
    cooldownSeconds,
    canResend: cooldownSeconds === 0 && !isResending,
    resendVerificationEmail,
  };
};
